import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ShortageSignal {
  soc_code: string;
  soc_title: string;
  pct_change: number;
  annual_openings: number;
  base_employment: number;
  proj_employment: number;
  outlook_band: string;
  bright_outlook: boolean;
  outlook_category: string | null;
  shortage_level: 'critical-shortage' | 'high-demand' | 'moderate-demand' | 'normal';
  shortage_score: number;
}

interface UseShortageSignalsOptions {
  socCode?: string;
  socCodes?: string[];
  minScore?: number;
  limit?: number;
  shortageLevel?: string;
  enabled?: boolean;
}

export function useShortageSignals(options: UseShortageSignalsOptions = {}) {
  const { socCode, socCodes, minScore, limit, shortageLevel, enabled = true } = options;

  return useQuery({
    queryKey: ['shortage-signals', socCode, socCodes, minScore, limit, shortageLevel],
    queryFn: async () => {
      const body: Record<string, unknown> = {};
      if (socCode) body.soc_code = socCode;
      if (socCodes && socCodes.length > 0) body.soc_codes = socCodes;
      if (minScore) body.min_score = minScore;
      if (limit) body.limit = limit;
      if (shortageLevel) body.shortage_level = shortageLevel;

      const { data, error } = await supabase.functions.invoke('api-shortage-signals', { body });
      if (error) throw error;
      return data as { signals: ShortageSignal[]; count: number };
    },
    enabled,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

export function useShortageSignalForOccupation(socCode: string | undefined) {
  const { data, ...rest } = useShortageSignals({
    socCode,
    enabled: !!socCode,
  });

  return {
    ...rest,
    data: data?.signals?.[0] || null,
  };
}
